import { useRef, useState, useMemo } from "react"
import { FaPause, FaPlay } from "react-icons/fa"
import { FiHeadphones } from "react-icons/fi"
import styles from "../../index.module.less"
import { Icon, Text } from "components/common"
import { t } from "i18next"

export const WhatsappAudioMessage = ({ audioUrl, duration = 0, audioTranscription }) => {
  const resolvedTranscript = audioTranscription || t("Transcript not available")
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [showTranscript, setShowTranscript] = useState(false)

  const formattedDuration = useMemo(() => {
    const dur = typeof duration === "string" ? parseFloat(duration) : duration
    if (!dur || dur <= 0) return "0:00"
    const m = Math.floor(dur / 60)
    const s = Math.floor(dur % 60)
    return `${m}:${s.toString().padStart(2, "0")}`
  }, [duration])

  const formattedCurrentTime = useMemo(() => {
    if (!currentTime) return ""
    const m = Math.floor(currentTime / 60)
    const s = Math.floor(currentTime % 60)
    return `${m}:${s.toString().padStart(2, "0")}`
  }, [currentTime])

  const handlePlayPause = () => {
    if (!audioRef.current) return
    if (playing) audioRef.current.pause()
    else audioRef.current.play()
  }

  const handleProgressClick = e => {
    if (!audioRef.current?.duration) return
    const rect = e?.currentTarget?.getBoundingClientRect()
    const clickX = e?.clientX - rect?.left
    audioRef.current.currentTime = (clickX / rect?.width) * audioRef.current.duration
  }

  const handleTimeUpdate = () => {
    if (!audioRef.current) return
    const { currentTime: ct, duration: dur } = audioRef.current
    setProgress(dur ? ct / dur : 0)
    setCurrentTime(ct)
  }

  if (!audioUrl)
    return (
      <Text className={styles.audioMsg} type="secondary">
        {t("Audio unavailable")}
      </Text>
    )

  return (
    <div className={styles.audioBubble}>
      <div className={styles.audioMsg}>
        <div className={styles.audioIconBox}>
          <FiHeadphones className={styles.audioIcon} />
          <span className={styles.audioDurationBox}>{formattedCurrentTime || formattedDuration}</span>
        </div>

        <button
          type="button"
          className={styles.audioPlayBtn}
          onClick={handlePlayPause}
          aria-label={playing ? "Pause audio" : "Play audio"}
        >
          {playing ? <FaPause /> : <FaPlay />}
        </button>

        <div className={styles.audioProgressBar} onClick={handleProgressClick} role="presentation">
          <div className={styles.audioProgress} style={{ width: `${progress * 100}%` }} />
        </div>
      </div>

      <div className={styles.audioDivider} role="presentation" />

      <button type="button" className={styles.audioTranscriptToggle} onClick={() => setShowTranscript(v => !v)}>
        <Icon icon="HiOutlineDocumentText" size={16} />
        <span>{showTranscript ? t("Hide Transcript") : t("View Transcript")}</span>
      </button>

      {showTranscript && <p className={styles.audioTranscriptCopy}>{resolvedTranscript}</p>}

      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setProgress(0)
          setCurrentTime(0)
        }}
        onTimeUpdate={handleTimeUpdate}
        style={{ display: "none" }}
      />
    </div>
  )
}
